"use client";

import React, { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import { fetchSessionsByDome, deleteSession } from "../../api/session.api";
import EditSessionModal from "./EditSessionModal";

const DomeSessions = () => {
  const location = useLocation();
  const domeData = location.state?.domeData || null;
  console.log("🚀 ~ DomeSessions ~ domeData:", domeData)
  const navigate = useNavigate();

  const { domeid: domeId } = useParams();
  const { background } = useContext(ThemeContext);
  const isDark = background === "dark";

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(9);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [dome, setDome] = useState(null); // dome info
  const [editSessionId, setEditSessionId] = useState(null); // modal open
  const [deleteSessionId, setDeleteSessionId] = useState(null); // confirmation modal

  // Fetch sessions
  const loadSessions = async (pageNumber = 1, query = "") => {
    try {
      if (pageNumber === 1) setLoading(true);
      else setIsLoadingMore(true);

      const data = await fetchSessionsByDome(domeId, query, pageNumber, limit);
      const fetchedSessions = data.sessions || data;

      if (pageNumber === 1) setSessions(fetchedSessions);
      else setSessions((prev) => [...prev, ...fetchedSessions]);

      setHasMore(data.hasNextPage || fetchedSessions.length === limit);
    } catch (error) {
      console.error("Error fetching sessions:", error);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

useEffect(() => {
  // Load sessions whenever domeId or search changes
  loadSessions(1, search);
}, [domeId, search]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const loadNextPage = () => {
    if (!hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    loadSessions(nextPage, search);
  };

  // Delete session
  const handleDelete = async () => {
    if (!deleteSessionId) return;
    try {
      await deleteSession(deleteSessionId);
      setSessions((prev) => prev.filter((s) => s._id !== deleteSessionId));
      setDeleteSessionId(null);
    } catch (err) {
      console.error(err);
      alert("Failed to delete session");
    }
  };

  if (loading)
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );

  return (
    <div className="container-fluid">
      {/* Header + Search */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h4 className="fs-20 mb-3">
          Sessions for{" "}
          <span className="text-primary">{domeData?.domeName || "N/A"}</span>
        </h4>

        <div className="d-flex gap-2 mb-3">
          <input
            type="text"
            placeholder="Search sessions..."
            value={search}
            onChange={handleSearchChange}
            className="form-control"
            style={{ width: "250px", borderRadius: "5px", fontSize: "14px" }}
          />

          <button
            className="btn btn-primary"
            style={{ fontSize: "14px", borderRadius: "5px" }}
            onClick={() =>
              navigate("create", { state: { domeData: domeData } })
            }
          >
            <i className="fas fa-plus me-2"></i> Create Session
          </button>
        </div>
      </div>

      {/* Sessions Table */}
      <div className="table-responsive shadow-sm rounded">
        <table
          className={`table align-middle mb-0 ${
            isDark ? "table-dark table-striped" : "table-striped"
          }`}
        >
          <thead className={`${isDark ? "bg-secondary" : "bg-light"}`}>
            <tr>
              <th scope="col" style={{ width: "5%" }}>
                #
              </th>
              <th scope="col">Session Name</th>
              <th scope="col">Start Time</th>
              <th scope="col">Duration (mins)</th>
              <th scope="col" style={{ width: "15%" }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {sessions.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-4 text-muted">
                  <i className="fas fa-calendar-times me-2"></i>No sessions
                  found.
                </td>
              </tr>
            ) : (
              sessions.map((session, index) => (
                <tr key={session._id}>
                  <td>{index + 1}</td>
                  <td className="fw-semibold text-primary">
                    {session.name || "Untitled Session"}
                  </td>
                  <td>{session.startTime || "N/A"}</td>
                  <td>{session.duration || "N/A"}</td>
                  <td className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setEditSessionId(session._id)}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => setDeleteSessionId(session._id)}
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {sessions.length > 0 && (
        <div className="d-flex justify-content-center gap-2 my-4">
          <button
            onClick={loadNextPage}
            disabled={!hasMore || isLoadingMore}
            className="btn btn-outline-primary"
          >
            {isLoadingMore
              ? "Loading..."
              : hasMore
              ? "Load More"
              : "No More Sessions"}
          </button>
        </div>
      )}

      {/* Edit Session Modal */}
      {editSessionId && (
        <EditSessionModal
          sessionId={editSessionId}
          dome={domeData}
          onClose={() => setEditSessionId(null)}
          onUpdated={() => loadSessions(1, search)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteSessionId && (
        <>
          {/* Backdrop */}
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1040 }}
            onClick={() => setDeleteSessionId(null)} // optional: click outside to close
          ></div>

          {/* Modal */}
          <div
            className="modal show d-block"
            tabIndex="-1"
            style={{ zIndex: 1050 }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title text-danger">Confirm Delete</h5>
                  <button
                    className="btn-close"
                    onClick={() => setDeleteSessionId(null)}
                  ></button>
                </div>
                <div className="modal-body">
                  Are you sure you want to delete this session?
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setDeleteSessionId(null)}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-danger" onClick={handleDelete}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DomeSessions;
